import React, { useEffect, useContext, useRef, useState } from "react";
import { IScreen, ThemeType } from "../types";
import "../styles/style.css";
import { FileSystem } from "../";
import { v4 as uuidv4 } from "uuid";
import { Auth } from "../../../contexts/ridersUserContext";

export const ScreenInfo = React.createContext({});

export const Screen: IScreen = ({ children, project, env }) => {
  const block = project.dev === "blockly";
  const [theme, setTheme] = useState<ThemeType>(
    (localStorage.getItem("riders-theme") as ThemeType) ?? "light"
  );
  const [collapsedSidebar, setCollapsedSidebar] = useState<boolean>(false);

  const [filePath, setFilePath] = useState<string>("");
  const [blocklySource, setBlocklySource] = useState<string>("");

  const [workspaces, setWorkspaces] = React.useState<any>([]);
  const [activeChapter, setActiveChapter] = React.useState<any>();
  const [chapterKey, setChapterKey] = React.useState<any>(uuidv4());
  const [yamlData, setData] = useState<any>([]);

  const [isTerminalOpen, setIsTerminalOpen] = React.useState(false);

  const [blocklyXML, setBlocklyXML] = React.useState<string | undefined>(
    undefined
  );

  const [pythonCode, setPythonCode] = React.useState(undefined);
  const [editorFocus, setEditorFocus] = React.useState<any>(undefined);

  const [isDragging, setIsDragging] = React.useState(false);
  const [mousePosition, setMousePosition] = React.useState<any>(undefined);
  const [cursor, setCursor] = React.useState("default");
  const [refreshKey, setRefreshKey] = React.useState(uuidv4());

  const [blocklyKey, setBlocklyKey] = React.useState<string | null>(
    project.toolboxPath ? uuidv4() : null
  );

  const { readFile, updateFile } = useContext<any>(FileSystem.context);

  const { yamlData: baseYamlData } = useContext<any>(Auth.context);

  useEffect(() => {
    setData(baseYamlData);
  }, [baseYamlData]);

  const godotContainerRef = useRef<any>(null);
  const documentationRef = useRef<any>(null);

  // listen and set mouse position
  const mouseMoveListener = (e: any) => {
    if (e.clientX / window.innerWidth > 0.85) {
      setMousePosition("right");
    } else if (e.clientY / window.innerHeight > 0.75) {
      setMousePosition("bottom");
    } else {
      setMousePosition(undefined);
    }
  };

  // add mousemove listener
  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", mouseMoveListener);
      return () => {
        window.removeEventListener("mousemove", mouseMoveListener);
        setMousePosition(undefined);
      };
    }
  }, [isDragging]);

  // Save blockly xml to file
  useEffect(() => {
    if (blocklyXML === undefined) {
      return;
    }

    readFile(`/${blocklySource}`).then((xml: string) => {
      if (xml === blocklyXML) {
        return;
      }
      updateFile(`/${blocklySource}`, blocklyXML);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocklyXML]);

  // Save blockly python code to file
  useEffect(() => {
    if (block) {
      if (pythonCode === undefined) {
        return;
      }
      readFile(`${filePath}`).then((code: string) => {
        if (code === pythonCode) {
          return;
        }
        updateFile(`${filePath}`, pythonCode);
        console.log("python code updated:", filePath);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pythonCode]);

  const refreshFunction = () => {
    setRefreshKey(uuidv4());
  };

  return (
    <ScreenInfo.Provider
      value={{
        theme,
        setTheme,
        baseYamlData,
        setActiveChapter,
        activeChapter,
        setCursor,
        setWorkspaces,
        isTerminalOpen,
        setIsTerminalOpen,
        workspaces,
        blocklyXML,
        setBlocklyXML,
        setPythonCode,
        pythonCode,
        setEditorFocus,
        setIsDragging,
        isDragging,
        mousePosition,
        project,
        block,
        godotContainerRef,
        documentationRef,
        env,
        refreshKey,
        refreshFunction,
        yamlData,
        setData,
        filePath,
        setFilePath,
        blocklySource,
        setBlocklySource,
        collapsedSidebar,
        setCollapsedSidebar,
        chapterKey,
        setChapterKey,
        blocklyKey,
        setBlocklyKey
      }}
    >
      <div
        className={`w-screen h-screen overflow-x-hidden overflow-y-scroll scrollbar-hidden`}
        style={{ cursor }}
      >
        {children}
      </div>
    </ScreenInfo.Provider>
  );
};

Screen.context = ScreenInfo;
